import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import FormPage from '../../components/ui/FormPage.tsx';
import StatusChip from '../../components/ui/StatusChip.tsx';

const ConciliarOfx: React.FC = () => {
  const { txId } = useParams<{ txId: string }>();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string>('');
  const [createFromOfx, setCreateFromOfx] = useState(false);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery(
    ['ofx-tx', txId],
    async () => {
      const res = await api.get(`/financeiro/conciliacao-ofx/${txId}`);
      return res.data as any;
    },
    {
      enabled: !!txId,
      onSuccess: (d) => {
        if (d?.suggestedMovementId) setSelected(String(d.suggestedMovementId));
      },
    }
  );

  const mutation = useMutation(
    async () => {
      const res = await api.post(`/financeiro/conciliacao-ofx/${txId}/conciliar`, {
        cashMovementId: createFromOfx ? undefined : selected || undefined,
        createFromOfx,
      });
      return res.data;
    },
    {
      onSuccess: () => navigate('/financeiro/conciliacao-ofx'),
      onError: (err: any) => setError(err?.response?.data?.error || 'Erro ao conciliar'),
    }
  );

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  const canConfirm = createFromOfx || !!selected;

  return (
    <FormPage
      backTo="/financeiro/conciliacao-ofx"
      backLabel="Voltar ao extrato"
      breadcrumb="Financeiro / Conciliação OFX / Conciliar"
      title="Conciliar transação"
      subtitle="Correspondência por valor + data ±3 dias"
      hint={`FITID ${data.fitid} · ${new Date(data.postedAt + 'T00:00:00').toLocaleDateString('pt-BR')} · ${formatCurrency(data.amount)}`}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/financeiro/conciliacao-ofx')}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canConfirm || mutation.isLoading}
            onClick={() => {
              setError('');
              mutation.mutate();
            }}
          >
            {mutation.isLoading ? 'Confirmando…' : 'Confirmar conciliação'}
          </button>
        </>
      }
    >
      {error && <div className="field-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="form-section-title">Transação do banco</div>
      <div className="summary-panel" style={{ marginBottom: 20 }}>
        <div className="summary-row">
          <span>Descrição</span>
          <strong>{data.description}</strong>
        </div>
        <div className="summary-row">
          <span>Valor</span>
          <strong>
            {data.type === 'in' ? '+ ' : '− '}
            {formatCurrency(data.amount)}
          </strong>
        </div>
        <div className="summary-row">
          <span>Status</span>
          <StatusChip label={data.statusLabel} tone={data.statusTone} />
        </div>
      </div>

      <div className="form-section-title">Conciliar com lançamento existente</div>
      <div className="table-wrap" style={{ marginBottom: 16 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th></th>
              <th>LC</th>
              <th>Descrição</th>
              <th>Data</th>
              <th style={{ textAlign: 'right' }}>Valor</th>
            </tr>
          </thead>
          <tbody>
            {(data.candidates || []).map((c: any) => (
              <tr key={c.id}>
                <td>
                  <input
                    type="radio"
                    name="candidate"
                    checked={!createFromOfx && selected === c.id}
                    onChange={() => {
                      setCreateFromOfx(false);
                      setSelected(c.id);
                    }}
                  />
                </td>
                <td>
                  {c.lcCode}
                  {c.suggested && (
                    <span style={{ marginLeft: 6 }}>
                      <StatusChip label="Sugerido" tone="amber" />
                    </span>
                  )}
                </td>
                <td>{c.description}</td>
                <td className="mono">
                  {new Date(c.movementDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                </td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(c.amount)}</td>
              </tr>
            ))}
            {(data.candidates || []).length === 0 && (
              <tr>
                <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  Nenhum candidato com valor e data ±3 dias.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={createFromOfx}
          onChange={e => {
            setCreateFromOfx(e.target.checked);
            if (e.target.checked) setSelected('');
          }}
        />
        <span>Criar lançamento a partir do OFX e já conciliar</span>
      </label>
    </FormPage>
  );
};

export default ConciliarOfx;
